import { propTypes, Spinner } from '@tabeo/scarf'
import SideNavbar from 'components/layouts/SideNavbar'
import Navbar from 'components/Navbar'
import config from 'config'
import RequireSettingsAccess from 'pages/Merchant/Settings/RequireSettingsAccess'
import PasswordChanged from 'pages/PasswordChanged'
import Ticket from 'pages/Ticket'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useMatch } from 'react-router'
import { useAuth } from 'resources/Auth'
import RequireAuth from './PrivateRoute'

const Login = lazy(() => import('pages/Login'))
const Settings = lazy(() => import('pages/Settings'))
const Account = lazy(() => import('pages/Account'))
const Dashboard = lazy(() => import('pages/Dashboard'))
const Document = lazy(() => import('pages/Document'))

const Transaction = lazy(() => import('pages/Transaction'))
const Transactions = lazy(() => import('pages/Transactions'))

const TerminalPayment = lazy(() => import('pages/TerminalPayment'))
const TerminalPayments = lazy(() => import('pages/TerminalPayments'))

const LinkPayment = lazy(() => import('pages/LinkPayment'))
const LinkPayments = lazy(() => import('pages/LinkPayments'))

const Refunds = lazy(() => import('pages/Refunds'))

const Merchant = lazy(() => import('pages/Merchant'))
const Merchants = lazy(() => import('pages/Merchants'))
const MerchantSettings = lazy(() => import('pages/Merchant/Settings'))
const MerchantSettingsRiskProfilesLoanLimits = lazy(() =>
  import('pages/Merchant/Settings/RiskProfiles/LoanLimits')
)
const FeeGroup = lazy(() => import('pages/Merchant/FeeGroup'))
const PaymentOptions = lazy(() =>
  import('pages/Merchant/Settings/RiskProfiles/PaymentOptions')
)
const Product = lazy(() => import('pages/Product'))

const Lead = lazy(() => import('pages/Lead'))
const Leads = lazy(() => import('pages/Leads'))

const Tickets = lazy(() => import('pages/Tickets'))

const Subscription = lazy(() => import('pages/Subscription'))
const Subscriptions = lazy(() => import('pages/Subscriptions'))
const Plan = lazy(() => import('pages/Plan'))

const SubscriptionV2 = lazy(() => import('pages/SubscriptionV2'))
const PlansV2Members = lazy(() => import('pages/PlansV2/Members'))
const NewSubscriptionV2 = lazy(() => import('pages/NewSubscriptionV2'))

const Bill = lazy(() => import('pages/Bill'))
const Bills = lazy(() => import('pages/Bills'))

const Payout = lazy(() => import('pages/Payout'))
const Payouts = lazy(() => import('pages/Payouts'))
const MerchantPayout = lazy(() => import('pages/MerchantPayout'))
const MerchantPayouts = lazy(() => import('pages/MerchantPayouts'))
const BlockedPayouts = lazy(() => import('pages/BlockedPayouts'))
const FinanceLedgerUploads = lazy(() => import('pages/FinanceLedgerUploads'))
const FinanceLedgerUpload = lazy(() => import('pages/FinanceLedgerUpload'))
const FinancePricing = lazy(() => import('pages/FinancePricing'))
const FinancePricingDetail = lazy(() => import('pages/FinancePricing/Detail'))
const FinancePricingHistory = lazy(() => import('pages/FinancePricing/History'))
const SalesTools = lazy(() => import('pages/SalesTools'))

const ArrearsCases = lazy(() => import('pages/ArrearsCases'))
const CollectionAccount = lazy(() => import('pages/CollectionAccount'))
const CollectAPayment = lazy(() => import('pages/Transaction/CollectAPayment'))
const ChangePaymentDayPage = lazy(() =>
  import('pages/Transaction/ChangePaymentDay')
)
const ChangePaymentMethodPage = lazy(() =>
  import('pages/Transaction/ChangePaymentMethod')
)

const Consumer = lazy(() => import('pages/Consumer'))

const Instruction = lazy(() => import('pages/Instruction'))
const CreateInstruction = lazy(() => import('pages/Instruction/Create'))
const InstructionPayouts = lazy(() => import('pages/Instruction/Payouts'))
const Instructions = lazy(() => import('pages/Instructions'))

const Clinician = lazy(() => import('pages/Clinician'))
const Treatment = lazy(() => import('pages/Treatment'))

const DentistPages = lazy(() => import('pages/DentistPages'))

const ScarfcnTest = lazy(() => import('pages/ScarfcnTest'))

function Layout({ children }) {
  const {
    data: { token },
  } = useAuth()
  // useMatch is exact + single-pattern; the v5 useRouteMatch took an array and
  // matched prefixes. Call each unconditionally and OR the results; /* keeps
  // the prefix match for documents. These pages render chrome-less.
  const documentsMatch = useMatch('/documents/:id/*')
  const passwordChangedMatch = useMatch('/password-changed')
  const match = documentsMatch || passwordChangedMatch

  if (match) {
    return children
  }

  if (token) {
    return <SideNavbar>{children}</SideNavbar>
  }
  return (
    <>
      <Navbar />
      {children}
    </>
  )
}

Layout.propTypes = {
  children: propTypes.children,
}

const MainRouter = () => {
  const {
    data: { user },
  } = useAuth()
  return (
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<Navigate to="/transactions" replace />} />
          <Route
            path="/stats"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/merchants"
            element={
              <RequireAuth>
                <Merchants />
              </RequireAuth>
            }
          />
          <Route
            path="/merchants/:merchantId/products/:productId"
            element={
              <RequireAuth>
                <RequireSettingsAccess>
                  <Product />
                </RequireSettingsAccess>
              </RequireAuth>
            }
          />
          <Route
            path="/merchants/:merchantId/fee"
            element={
              <RequireAuth>
                <RequireSettingsAccess>
                  <FeeGroup />
                </RequireSettingsAccess>
              </RequireAuth>
            }
          />
          <Route
            path="/merchants/:merchantId/settings/risk-profiles/:riskProfileName/loan-limits"
            element={
              <RequireAuth>
                <RequireSettingsAccess>
                  <MerchantSettingsRiskProfilesLoanLimits />
                </RequireSettingsAccess>
              </RequireAuth>
            }
          />
          <Route
            path="/merchants/:merchantId/settings/risk-profiles/:riskProfileName/payment-options"
            element={
              <RequireAuth>
                <RequireSettingsAccess>
                  <PaymentOptions />
                </RequireSettingsAccess>
              </RequireAuth>
            }
          />
          <Route
            path="/merchants/:merchantId/settings"
            element={
              <RequireAuth>
                <RequireSettingsAccess>
                  <MerchantSettings />
                </RequireSettingsAccess>
              </RequireAuth>
            }
          />
          <Route
            path="/merchants/:merchantId"
            element={
              <RequireAuth>
                <Merchant />
              </RequireAuth>
            }
          />
          <Route
            path="/transactions"
            element={
              <RequireAuth>
                <Transactions />
              </RequireAuth>
            }
          />
          <Route
            path="/terminal-payments/:terminalPaymentId"
            element={
              <RequireAuth>
                <TerminalPayment />
              </RequireAuth>
            }
          />
          <Route
            path="/terminal-payments"
            element={
              <RequireAuth>
                <TerminalPayments />
              </RequireAuth>
            }
          />
          <Route
            path="/link-payments/:linkPaymentId"
            element={
              <RequireAuth>
                <LinkPayment />
              </RequireAuth>
            }
          />
          <Route
            path="/link-payments"
            element={
              <RequireAuth>
                <LinkPayments />
              </RequireAuth>
            }
          />
          <Route
            path="/refunds"
            element={
              <RequireAuth>
                <Refunds />
              </RequireAuth>
            }
          />
          <Route
            path="/documents/:documentId"
            element={
              <RequireAuth>
                <Document />
              </RequireAuth>
            }
          />
          <Route
            path="/transactions/:transactionId/collect-a-payment"
            element={
              <RequireAuth>
                <CollectAPayment />
              </RequireAuth>
            }
          />
          <Route
            path="/transactions/:transactionId/change-payment-method"
            element={
              <RequireAuth>
                <ChangePaymentMethodPage />
              </RequireAuth>
            }
          />
          <Route
            path="/transactions/:transactionId/change-payment-day"
            element={
              <RequireAuth>
                <ChangePaymentDayPage />
              </RequireAuth>
            }
          />
          <Route
            path="/transactions/:transactionId"
            element={
              <RequireAuth>
                <Transaction />
              </RequireAuth>
            }
          />
          <Route
            path="/bills/:billId"
            element={
              <RequireAuth>
                <Bill />
              </RequireAuth>
            }
          />
          <Route
            path="/bills"
            element={
              <RequireAuth>
                <Bills />
              </RequireAuth>
            }
          />
          <Route
            path="/leads/:leadId"
            element={
              <RequireAuth>
                <Lead />
              </RequireAuth>
            }
          />
          <Route
            path="/leads"
            element={
              <RequireAuth>
                <Leads />
              </RequireAuth>
            }
          />
          <Route
            path="/settings/treatments/:treatmentId"
            element={
              <RequireAuth>
                <Treatment />
              </RequireAuth>
            }
          />
          <Route
            path="/settings/clinicians/:clinicianId"
            element={
              <RequireAuth>
                <Clinician />
              </RequireAuth>
            }
          />
          <Route
            path="/arrears-cases/*"
            element={
              <RequireAuth>
                <ArrearsCases />
              </RequireAuth>
            }
          />
          <Route
            path="/collection-accounts/:consumerId"
            element={
              <RequireAuth>
                <CollectionAccount />
              </RequireAuth>
            }
          />
          <Route
            path="/tickets"
            element={
              <RequireAuth>
                <Tickets />
              </RequireAuth>
            }
          />
          <Route
            path="/instructions/new"
            element={
              <RequireAuth>
                <CreateInstruction />
              </RequireAuth>
            }
          />
          <Route
            path="/instructions/:instructionId/files/:fileId/payouts"
            element={
              <RequireAuth>
                <InstructionPayouts />
              </RequireAuth>
            }
          />
          <Route
            path="/instructions/:instructionId"
            element={
              <RequireAuth>
                <Instruction />
              </RequireAuth>
            }
          />
          <Route
            path="/instructions"
            element={
              <RequireAuth>
                <Instructions />
              </RequireAuth>
            }
          />
          <Route
            path="/payouts/:payoutID"
            element={
              <RequireAuth>
                <Payout />
              </RequireAuth>
            }
          />
          <Route
            path="/payouts"
            element={
              <RequireAuth>
                <Payouts />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant-payouts/:payoutId"
            element={
              <RequireAuth>
                <MerchantPayout />
              </RequireAuth>
            }
          />
          <Route
            path="/merchant-payouts"
            element={
              <RequireAuth>
                <MerchantPayouts />
              </RequireAuth>
            }
          />
          <Route
            path="/blocked-payouts"
            element={
              <RequireAuth>
                <BlockedPayouts />
              </RequireAuth>
            }
          />
          <Route
            path="/finance-ledger/:ledgerUploadId"
            element={
              <RequireAuth>
                <FinanceLedgerUpload />
              </RequireAuth>
            }
          />
          <Route
            path="/finance-ledger"
            element={
              <RequireAuth>
                <FinanceLedgerUploads />
              </RequireAuth>
            }
          />
          <Route
            path="/finance-pricing/:cardId/history"
            element={
              <RequireAuth>
                <FinancePricingHistory />
              </RequireAuth>
            }
          />
          <Route
            path="/finance-pricing/:cardId/versions/:versionNumber"
            element={
              <RequireAuth>
                <FinancePricingDetail />
              </RequireAuth>
            }
          />
          <Route
            path="/finance-pricing/:cardId"
            element={
              <RequireAuth>
                <FinancePricingDetail />
              </RequireAuth>
            }
          />
          <Route
            path="/finance-pricing"
            element={
              <RequireAuth>
                <FinancePricing />
              </RequireAuth>
            }
          />
          <Route
            path="/plans-v2/members"
            element={
              <RequireAuth>
                <PlansV2Members />
              </RequireAuth>
            }
          />
          <Route
            path="/sales-tools/plans"
            element={
              <RequireAuth>
                <SalesTools tool="plans" />
              </RequireAuth>
            }
          />
          <Route
            path="/sales-tools/finance"
            element={
              <RequireAuth>
                <SalesTools tool="finance" />
              </RequireAuth>
            }
          />
          <Route
            path="/subscriptions-v2/:subscriptionId"
            element={
              <RequireAuth>
                <SubscriptionV2 />
              </RequireAuth>
            }
          />
          <Route
            path="/new-subscription-v2"
            element={
              <RequireAuth>
                <NewSubscriptionV2 />
              </RequireAuth>
            }
          />
          <Route
            path="/subscriptions/:subscriptionId"
            element={
              <RequireAuth>
                <Subscription />
              </RequireAuth>
            }
          />
          <Route
            path="/plans/:planId"
            element={
              <RequireAuth>
                <Plan />
              </RequireAuth>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <RequireAuth>
                <Subscriptions />
              </RequireAuth>
            }
          />
          <Route
            path="/dentist-pages"
            element={
              <RequireAuth>
                <DentistPages />
              </RequireAuth>
            }
          />
          <Route
            path="/consumers/:consumerId"
            element={
              <RequireAuth>
                <Consumer />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <Account />
              </RequireAuth>
            }
          />
          {config.ENV !== 'production' && (
            <Route path="/scarfcn-test" element={<ScarfcnTest />} />
          )}
          <Route path="/login" element={<Login />} />
          <Route path="/password-changed" element={<PasswordChanged />} />
        </Routes>
        {user && <Ticket />}
      </Suspense>
    </Layout>
  )
}

export default MainRouter
